import PropTypes from "prop-types";
import React, { useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import { offsetDateForBrowser, offsetDateForServer } from "../../../../shared/timeZoneLogic";
import { toastError, errorText } from "../../../../shared/toastHelper";

const flatPickrFormat = "n/j/y at h:iK";

const modalContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "12px",
  boxShadow: "0 8px 8px -4px rgba(10,13,18,0.03), 0 20px 24px -4px rgba(10,13,18,0.08)",
  width: "min(520px, calc(100vw - 32px))",
  padding: "24px",
  background: "var(--sl-bg-primary)",
};

const REASON_MAX_LENGTH = 500;

export const alreadyHappenedLines = (sideEffects) => {
  if (!sideEffects) return [];

  const lines = [];
  const followUp = sideEffects.post_event_follow_up || {};
  const workflows = sideEffects.event_ended_workflows || {};
  const webhooks = sideEffects.order_after_attendance_webhooks || {};
  const endedEmail = sideEffects.confirm_has_ended_email || {};

  lines.push(
    followUp.already_sent > 0
      ? `A post-event follow-up email was sent to ${followUp.fans_already_sent} fan${followUp.fans_already_sent === 1 ? "" : "s"}. That cannot be recalled.`
      : "No post-event follow-up email has reached fans."
  );

  if (followUp.cancellable > 0) {
    lines.push(`${followUp.cancellable} post-event follow-up email${followUp.cancellable === 1 ? " is" : "s are"} still scheduled and will be cancelled.`);
  }

  const inFlight = (followUp.still_pending || 0) - (followUp.cancellable || 0);
  if (inFlight > 0) {
    lines.push(`${inFlight} post-event follow-up email${inFlight === 1 ? " is" : "s are"} already being sent and can no longer be stopped.`);
  }

  lines.push(
    workflows.triggered > 0
      ? `${workflows.triggered} “event ended” automation${workflows.triggered === 1 ? "" : "s"} started${workflows.names && workflows.names.length ? ` (${workflows.names.join(", ")})` : ""}. Any messages already sent cannot be recalled.`
      : "No “event ended” automations ran."
  );

  lines.push(
    webhooks.created > 0
      ? `${webhooks.created} post-attendance update${webhooks.created === 1 ? " was" : "s were"} generated for your connected integrations.`
      : "No updates were sent to connected integrations."
  );

  lines.push(
    endedEmail.recipients > 0
      ? `Around ${endedEmail.recipients} team member${endedEmail.recipients === 1 ? "" : "s"} may have been emailed that the event ended.`
      : "No one on your team was emailed that the event ended."
  );

  return lines;
};

const UnpastModal = ({ open, onClose, teamId, confirm, blockers, sideEffects, csrfToken, onRestored }) => {
  const timeZone = confirm.timeZone;
  const [endTime, setEndTime] = useState(null);
  const [doorTime, setDoorTime] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isBlocked = Array.isArray(blockers) && blockers.length > 0;
  const canSubmit = !isBlocked && !busy && !!endTime && reason.trim().length > 0
    && reason.length <= REASON_MAX_LENGTH;

  const submit = () => {
    if (!canSubmit) return;
    setBusy(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios
      .post(`/teams/${teamId}/confirms/${confirm.id}/unpast`, {
        confirm: {
          end_time: offsetDateForServer(endTime, timeZone),
          door_time: doorTime ? offsetDateForServer(doorTime, timeZone) : null,
          reason: reason.trim(),
        },
      })
      .then(() => { onRestored(); })
      .catch((e) => toastError(errorText(e, "Unable to move this event out of Past.")))
      .finally(() => setBusy(false));
  };

  return (
    <Popup modal open={open} closeOnDocumentClick onClose={onClose} contentStyle={modalContentStyle}>
      <div className="event-actions-dialog">
        <h5 className="event-actions-dialog-title">Move this event out of Past?</h5>

        {isBlocked ? (
          <>
            <p className="event-actions-dialog-body">
              This event cannot be moved out of Past. Contact support if it still needs to be
              corrected.
            </p>
            <ul className="event-actions-dialog-body">
              {blockers.map((blocker) => (<li key={blocker}>{blocker}</li>))}
            </ul>
          </>
        ) : (
          <>
            <p className="event-actions-dialog-body">
              This event is showing as Past even though it has not happened yet. Set its real end
              time to put it back on your active calendar.
            </p>

            <label className="setlist-text-sm" htmlFor="unpast-end-time">Event end date and time</label>
            <div className="flatpickr-date-input px-2 d-flex align-items-center" style={{ marginBottom: "12px" }}>
              <div className="pr-2" style={{ fontSize: "18px" }}>
                <i className="far fa-calendar" />
              </div>
              <Flatpickr
                id="unpast-end-time"
                options={{ enableTime: true, dateFormat: flatPickrFormat, minDate: offsetDateForBrowser(new Date(), timeZone) }}
                placeholder="Select the real end date and time"
                value={endTime}
                onClose={(dates) => setEndTime(dates[0] || null)}
              />
            </div>

            <label className="setlist-text-sm" htmlFor="unpast-door-time">Doors date and time (only if it is also wrong)</label>
            <div className="flatpickr-date-input px-2 d-flex align-items-center" style={{ marginBottom: "12px" }}>
              <div className="pr-2" style={{ fontSize: "18px" }}>
                <i className="far fa-calendar" />
              </div>
              <Flatpickr
                id="unpast-door-time"
                options={{ enableTime: true, dateFormat: flatPickrFormat }}
                placeholder="Leave blank to keep it as it is"
                value={doorTime}
                onClose={(dates) => setDoorTime(dates[0] || null)}
              />
            </div>

            <label className="setlist-text-sm" htmlFor="unpast-reason">Why is this being corrected?</label>
            <textarea
              id="unpast-reason"
              className="form-control"
              rows="2"
              maxLength={REASON_MAX_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ marginBottom: "16px" }}
            />

            <p className="event-actions-dialog-body"><strong>What already happened</strong></p>
            <ul className="event-actions-dialog-body">
              {alreadyHappenedLines(sideEffects).map((line) => (<li key={line}>{line}</li>))}
            </ul>
          </>
        )}

        <div className="event-actions-dialog-buttons">
          <button type="button" className="event-actions-btn-secondary" onClick={onClose}>
            {isBlocked ? "Close" : "Cancel"}
          </button>
          {!isBlocked && (
            <button type="button" className="event-actions-btn-primary" disabled={!canSubmit} onClick={submit}>
              Move out of Past
            </button>
          )}
        </div>
      </div>
    </Popup>
  );
};

UnpastModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirm: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    timeZone: PropTypes.string,
  }).isRequired,
  blockers: PropTypes.arrayOf(PropTypes.string),
  sideEffects: PropTypes.object,
  csrfToken: PropTypes.string,
  onRestored: PropTypes.func.isRequired,
};

export default UnpastModal;
